import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import StoreIcon from '@mui/icons-material/Store';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { toast } from 'react-toastify';
import { api, useAuth } from '../services/AuthService';
import { escapeHtml } from '../utils/escapeHtml';

function brl(value) {
  return `R$ ${Number(value || 0).toFixed(2)}`;
}

function paymentInfo(delivery) {
  const method = String(delivery?.payment_method || '').trim().toLowerCase();
  if (method === 'money' || method === 'dinheiro') {
    const change = delivery?.needs_change && Number(delivery?.change_amount) > 0
      ? ` • troco para ${brl(delivery.change_amount)}`
      : '';
    return { label: `Dinheiro${change}`, color: 'success' };
  }
  if (method === 'pix') return { label: 'PIX online', color: 'info' };
  if (['card', 'cartao', 'credit_card', 'debit_card'].includes(method)) {
    return { label: 'Cartão online', color: 'primary' };
  }
  if (method === 'mercadopago') return { label: 'Mercado Pago', color: 'info' };
  if (method === 'pickup') return { label: 'Retirada no local', color: 'default' };
  if (method === 'mesa') return { label: 'Mesa', color: 'default' };
  if (method === 'delivery') return { label: 'Pagamento na entrega', color: 'warning' };
  return { label: delivery?.payment_method || 'Não informado', color: 'default' };
}

function MotoboyDeliveries() {
  const { isAdminMaster } = useAuth();
  const [activeTab, setActiveTab] = useState(isAdminMaster() ? 0 : 1);
  const [motoboys, setMotoboys] = useState([]);
  const [selectedMotoboy, setSelectedMotoboy] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  
  // Subsídios
  const [subsidies, setSubsidies] = useState([]);
  const [loadingSubsidies, setLoadingSubsidies] = useState(false);
  const [filterLanchonete, setFilterLanchonete] = useState('all');

  const loadMotoboys = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/motoboys');
      const appMotoboys = (Array.isArray(response.data) ? response.data : [])
        .filter((motoboy) => String(motoboy.motoboy_scope || '').toUpperCase() === 'APP');
      setMotoboys(appMotoboys);

      if (!selectedMotoboy && appMotoboys.length > 0) {
        setSelectedMotoboy(appMotoboys[0]);
      }
    } catch (error) {
      console.error('Erro ao carregar motoboys APP:', error);
      setMotoboys([]);
    } finally {
      setLoading(false);
    }
  }, [selectedMotoboy]);

  const loadDeliveries = useCallback(async (motoboy) => {
    if (!motoboy?.id) {
      setDeliveries([]);
      return;
    }

    try {
      setLoadingDeliveries(true);
      const response = await api.get(`/motoboys/${motoboy.id}/deliveries-by-date?all=true`);
      setDeliveries(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar entregas do motoboy:', error);
      setDeliveries([]);
    } finally {
      setLoadingDeliveries(false);
    }
  }, []);

  const loadSubsidies = useCallback(async () => {
    try {
      setLoadingSubsidies(true);
      const response = await api.get('/pedidos/admin/subsidies');
      setSubsidies(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar relatório de subsídios:', error);
      setSubsidies([]);
    } finally {
      setLoadingSubsidies(false);
    }
  }, []);

  useEffect(() => {
    loadMotoboys();
  }, [loadMotoboys]);

  useEffect(() => {
    loadDeliveries(selectedMotoboy);
  }, [loadDeliveries, selectedMotoboy]);

  useEffect(() => {
    if (activeTab === 1) {
      loadSubsidies();
    }
  }, [activeTab, loadSubsidies]);

  const summary = useMemo(() => {
    const lanchonetes = new Set(deliveries.map((delivery) => delivery.lanchonete_name).filter(Boolean));
    return {
      totalDeliveries: deliveries.length,
      totalEarnings: deliveries.reduce((sum, delivery) => sum + Number(delivery.delivery_fee || 0), 0),
      totalLanchonetes: lanchonetes.size
    };
  }, [deliveries]);

  // Subsídios memoizados
  const filteredSubsidies = useMemo(() => {
    if (filterLanchonete === 'all') return subsidies;
    return subsidies.filter(s => Number(s.lanchonete_id) === Number(filterLanchonete));
  }, [subsidies, filterLanchonete]);

  const lanchonetesList = useMemo(() => {
    const list = [];
    const seen = new Set();
    subsidies.forEach(s => {
      if (!seen.has(s.lanchonete_id)) {
        seen.add(s.lanchonete_id);
        list.push({ id: s.lanchonete_id, name: s.lanchonete_name });
      }
    });
    return list;
  }, [subsidies]);

  const subsidiesSummary = useMemo(() => {
    return {
      totalOrders: filteredSubsidies.length,
      totalAmount: filteredSubsidies.reduce((sum, s) => sum + Number(s.delivery_subsidy_owed || 0), 0)
    };
  }, [filteredSubsidies]);

  const handlePrintSubsidies = () => {
    const w = window.open('', '_blank');
    if (!w) {
      toast.error('Permita pop-ups para imprimir o relatório');
      return;
    }
    const totalSubsidio = subsidiesSummary.totalAmount;
    const qtdPedidos = subsidiesSummary.totalOrders;
    const nomeLanchonete = !isAdminMaster() && filteredSubsidies[0]
      ? filteredSubsidies[0].lanchonete_name
      : filterLanchonete === 'all'
      ? 'Todas as Lanchonetes'
      : lanchonetesList.find(l => Number(l.id) === Number(filterLanchonete))?.name || 'Lanchonete';

    const html = `
      <html>
        <head>
          <title>Relatório de Taxas de Frete Grátis</title>
          <style>
            * {
              color: #000000 !important;
              font-weight: 900 !important;
              box-sizing: border-box;
            }
            body { 
              font-family: 'Segoe UI', Arial, sans-serif; 
              padding: 5px; 
              width: 240px;
              margin: 0 auto;
            }
            .header { text-align: center; margin-bottom: 15px; border-bottom: 2.5px dashed #000000; padding-bottom: 10px; }
            .header h1 { margin: 0; font-size: 15px; text-transform: uppercase; }
            .header p { margin: 5px 0 0 0; font-size: 11px; }
            
            .summary-container { display: block; margin-bottom: 15px; }
            .summary-card { border: 2.5px solid #000000; padding: 10px; border-radius: 6px; background-color: #ffffff; text-align: center; margin-bottom: 8px; }
            .summary-card h3 { margin: 0 0 5px 0; font-size: 11px; text-transform: uppercase; }
            .summary-card p { margin: 0; font-size: 18px; }

            .section-title { font-size: 12px; margin: 15px 0 5px 0; border-bottom: 2.5px solid #000000; padding-bottom: 3px; text-transform: uppercase; }
            
            .pedidos-list { margin-top: 10px; }
            .pedido-box { border: 1.5px solid #000000; border-radius: 4px; padding: 6px; margin-bottom: 8px; font-size: 10px; }
            .pedido-header { display: flex; justify-content: space-between; font-weight: 900; border-bottom: 1px dashed #000000; padding-bottom: 3px; margin-bottom: 3px; }
            .pedido-details { display: flex; justify-content: space-between; margin-bottom: 2px; }
            .pedido-footer { display: flex; justify-content: space-between; font-weight: 900; border-top: 1px dotted #000000; padding-top: 2px; margin-top: 2px; }
            
            @media print {
              body { padding: 0; width: 100%; max-width: 240px; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Relatório de Taxas de Frete Grátis</h1>
            <p>Estabelecimento: ${escapeHtml(nomeLanchonete)}</p>
            <p>Emissão: ${new Date().toLocaleDateString('pt-BR')} ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</p>
          </div>
          
          <div class="summary-container">
            <div class="summary-card">
              <h3>Qtd Pedidos Frete Grátis</h3>
              <p>${qtdPedidos}</p>
            </div>
            <div class="summary-card">
              <h3>${isAdminMaster() ? 'Total a Receber' : 'Total a Pagar'}</h3>
              <p>${brl(totalSubsidio)}</p>
            </div>
          </div>

          <div class="section-title">Detalhamento dos Pedidos</div>
          <div class="pedidos-list">
            ${filteredSubsidies.map(s => `
              <div class="pedido-box">
                <div class="pedido-header">
                  <span>Pedido #${s.id}</span>
                  <span>${new Date(s.created_at).toLocaleDateString('pt-BR')}</span>
                </div>
                ${isAdminMaster() ? `<div class="pedido-details"><span>Loja:</span> <span>${escapeHtml(s.lanchonete_name)}</span></div>` : ''}
                <div class="pedido-details">
                  <span>Entregador:</span>
                  <span>${escapeHtml(s.motoboy_name || 'Não atribuído')}</span>
                </div>
                <div class="pedido-details">
                  <span>Forma Pagamento:</span>
                  <span>${escapeHtml(String(s.payment_method || '').toUpperCase())}</span>
                </div>
                <div class="pedido-details">
                  <span>Valor do Pedido:</span>
                  <span>${brl(s.total)}</span>
                </div>
                <div class="pedido-footer">
                  <span>Taxa Devida:</span>
                  <span>${brl(s.delivery_subsidy_owed)}</span>
                </div>
              </div>
            `).join('')}
          </div>
          
          <script>
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
      </html>
    `;
    w.document.write(html);
    w.document.close();
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>;
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {isAdminMaster() ? 'Entregas dos Motoboys' : 'Minhas Taxas de Entrega'}
          </Typography>
          <Typography color="text.secondary">
            {isAdminMaster() 
              ? 'Histórico completo de entregas e prestação de contas de frete grátis.'
              : 'Histórico de taxas de entrega subsidiadas por esta lanchonete (Frete Grátis).'}
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} variant="outlined" onClick={() => { if (isAdminMaster()) loadMotoboys(); loadSubsidies(); }}>
          Atualizar
        </Button>
      </Box>

      {isAdminMaster() && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)} color="primary">
            <Tab label="Entregas por Entregador" />
            <Tab label="Taxas de Entrega a Receber (Frete Grátis)" />
          </Tabs>
        </Box>
      )}

      {activeTab === 0 ? (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" mb={2}>Entregadores APP</Typography>
              {motoboys.length === 0 ? (
                <Typography color="text.secondary">Nenhum motoboy APP encontrado.</Typography>
              ) : (
                <List disablePadding>
                  {motoboys.map((motoboy, index) => (
                    <ListItemButton
                      key={motoboy.id}
                      selected={selectedMotoboy?.id === motoboy.id}
                      onClick={() => setSelectedMotoboy(motoboy)}
                      sx={{ borderRadius: 1, mb: 0.5 }}
                    >
                      <ListItemText
                        primary={`${index + 1}. ${motoboy.name}`}
                        secondary={motoboy.phone || motoboy.email}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <TwoWheelerIcon color="primary" />
                    <Typography variant="caption" display="block" color="text.secondary">Entregas</Typography>
                    <Typography variant="h5">{summary.totalDeliveries}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <AttachMoneyIcon color="success" />
                    <Typography variant="caption" display="block" color="text.secondary">Ganho do Motoboy</Typography>
                    <Typography variant="h5">{brl(summary.totalEarnings)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <StoreIcon color="secondary" />
                    <Typography variant="caption" display="block" color="text.secondary">Lanchonetes</Typography>
                    <Typography variant="h5">{summary.totalLanchonetes}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Paper sx={{ p: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  {selectedMotoboy ? `Entregas de ${selectedMotoboy.name}` : 'Selecione um entregador'}
                </Typography>
                {loadingDeliveries && <CircularProgress size={22} />}
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Pedido</TableCell>
                      <TableCell>Lanchonete</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Pagamento</TableCell>
                      <TableCell>Data</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Ganho</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deliveries.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">Nenhuma entrega encontrada.</TableCell>
                      </TableRow>
                    ) : deliveries.map((delivery) => (
                      <TableRow key={delivery.id}>
                        <TableCell>#{delivery.id}</TableCell>
                        <TableCell>{delivery.lanchonete_name || '-'}</TableCell>
                        <TableCell>{delivery.customer_name || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={paymentInfo(delivery).label}
                            color={paymentInfo(delivery).color}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{new Date(delivery.created_at).toLocaleString('pt-BR')}</TableCell>
                        <TableCell><Chip label={delivery.status} size="small" /></TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                          {brl(delivery.delivery_fee)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      ) : (
        <Box>
          <Grid container spacing={3} mb={3} alignItems="center">
            {isAdminMaster() ? (
              <>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Filtrar por Lanchonete</InputLabel>
                    <Select
                      value={filterLanchonete}
                      onChange={(e) => setFilterLanchonete(e.target.value)}
                      label="Filtrar por Lanchonete"
                    >
                      <MenuItem value="all">Todas as Lanchonetes</MenuItem>
                      {lanchonetesList.map(l => (
                        <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" display="block" color="text.secondary">Qtd Pedidos Frete Grátis</Typography>
                      <Typography variant="h5">{subsidiesSummary.totalOrders}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" display="block" color="text.secondary">Total a Receber (Lanchonetes)</Typography>
                      <Typography variant="h5" sx={{ color: 'success.main', fontWeight: 'bold' }}>{brl(subsidiesSummary.totalAmount)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={6}>
                  <Card>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" display="block" color="text.secondary">Qtd Pedidos com Frete Grátis</Typography>
                      <Typography variant="h5">{subsidiesSummary.totalOrders}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" display="block" color="text.secondary">Total a Pagar à Plataforma</Typography>
                      <Typography variant="h5" sx={{ color: 'error.main', fontWeight: 'bold' }}>{brl(subsidiesSummary.totalAmount)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </>
            )}
          </Grid>

          <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                {isAdminMaster() 
                  ? 'Detalhamento dos Pedidos com Frete Grátis (Motoboy APP)' 
                  : 'Meus Pedidos com Frete Grátis (Taxas Devidas à Plataforma)'}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Button variant="outlined" color="primary" onClick={handlePrintSubsidies} size="small">
                  Imprimir Relatório
                </Button>
                {loadingSubsidies && <CircularProgress size={22} />}
              </Box>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Pedido</TableCell>
                    {isAdminMaster() && <TableCell>Lanchonete</TableCell>}
                    <TableCell>Entregador</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell>Método de Pagamento</TableCell>
                    <TableCell>Valor Total</TableCell>
                    <TableCell align="right">{isAdminMaster() ? 'Taxa Devida pela Loja' : 'Taxa de Entrega (Pagar)'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSubsidies.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isAdminMaster() ? 7 : 6} align="center">Nenhum subsídio pendente encontrado.</TableCell>
                    </TableRow>
                  ) : filteredSubsidies.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>#{s.id}</TableCell>
                      {isAdminMaster() && <TableCell>{s.lanchonete_name}</TableCell>}
                      <TableCell>{s.motoboy_name || 'Não atribuído'}</TableCell>
                      <TableCell>{new Date(s.created_at).toLocaleString('pt-BR')}</TableCell>
                      <TableCell>{String(s.payment_method || '').toUpperCase()}</TableCell>
                      <TableCell>{brl(s.total)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                        {brl(s.delivery_subsidy_owed)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}
    </Box>
  );
}

export default MotoboyDeliveries;
